import React, { useEffect, useRef, useReducer } from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Flex, Link, Box, Text
} from 'components/ui';
import { DebouncedScroll } from 'constants/events';
import Chevron from 'components/Chevron';
import UI from 'utils/UI';
import * as pdpAnalytics from 'analytics/productPage/pdpAnalytics';
import anaConsts from 'analytics/constants';
import Debounce from 'utils/Debounce';
import localeUtils from 'utils/LanguageLocale';
import ProductPageEN from 'pages/Product/locales/ProductPage_en_US';
import ProductPageFR from 'pages/Product/locales/ProductPage_fr_CA';

const getText = localeUtils.getLocalisedDataFromResource(ProductPageEN, ProductPageFR);

function fireAnalytics(analyticsLinkName) {
    return pdpAnalytics.trackLinkClick({
        eventStrings: [anaConsts.Event.EVENT_71],
        linkName: analyticsLinkName,
        actionInfo: analyticsLinkName
    });
}

const DEBOUNCE_CLICK_NAV_VALUE = 700;
const DEBOUNCE_SCROLL_VALUE = 10;
const SCROLL_OFFSET = -95;
const ROOT_MARGIN = '-20% 0% -80% 0%';
const DETAILS = 'details';
const SIMILAR = 'similar';
const QA = 'QandA';
const REVIEWS = 'ratings-reviews-container';

const MENU_ITEMS = [
    { label: getText('details'), anchor: DETAILS },
    { label: getText('similar'), anchor: SIMILAR },
    { label: getText('questions'), anchor: QA },
    { label: getText('reviews'), anchor: REVIEWS }
];

const NAV_INITIAL_STATE = { activeId: '', showNavBar: false, isNavigatingByClick: false };

function navReducer(state, action) {
    switch (action.type) {
        case 'SCROLL_UPDATE':
            return { ...state, showNavBar: action.showNavBar, activeId: MENU_ITEMS[0].anchor };
        case 'INTERSECTION':
            return { ...state, activeId: action.payload };
        case 'NAVIGATE':
            return { ...state, isNavigatingByClick: true, activeId: action.payload };
        case 'TOP_NAV':
            return { ...state, isNavigatingByClick: true };
        case 'RESET_CLICK_NAV':
            return { ...state, isNavigatingByClick: false };
        default:
            return state;
    }
}

const MobileStickyMenu = () => {
    const [state, dispatch] = useReducer(navReducer, NAV_INITIAL_STATE);
    const observerRef = useRef(null);
    const isNavigatingByClickRef = useRef(false);
    isNavigatingByClickRef.current = state.isNavigatingByClick;
    const showNavBarRef = useRef(false);

    const debounceScrollRef = useRef(
        Debounce.debounce(id => {
            UI.getScrollSmooth(id, SCROLL_OFFSET);
        }, DEBOUNCE_SCROLL_VALUE)
    );

    const debounceResetRef = useRef(
        Debounce.debounce(() => {
            dispatch({ type: 'RESET_CLICK_NAV' });
        }, DEBOUNCE_CLICK_NAV_VALUE)
    );

    useEffect(() => {
        const handleScroll = () => {
            const nextShowNavBar = window.scrollY > 930;

            if (showNavBarRef.current !== nextShowNavBar) {
                showNavBarRef.current = nextShowNavBar;
                dispatch({ type: 'SCROLL_UPDATE', showNavBar: nextShowNavBar });
            }
        };

        window.addEventListener(DebouncedScroll, handleScroll);

        observerRef.current = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting && !isNavigatingByClickRef.current) {
                        dispatch({ type: 'INTERSECTION', payload: entry.target.id });
                    }
                });
            },
            { rootMargin: ROOT_MARGIN }
        );

        MENU_ITEMS.forEach(section => {
            const element = document.getElementById(section.anchor);

            if (element) {
                observerRef.current.observe(element);
            }
        });

        return () => {
            window.removeEventListener(DebouncedScroll, handleScroll);

            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, []);

    const navigateToAnchor = id => () => {
        dispatch({ type: 'NAVIGATE', payload: id });
        debounceScrollRef.current(id);
        debounceResetRef.current();
    };

    const navigateToTop = () => {
        dispatch({ type: 'TOP_NAV' });
        debounceResetRef.current();
        fireAnalytics(anaConsts.LinkData.BACK_TO_TOP);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    if (!state.showNavBar) {
        return null;
    }

    return (
        <Box
            css={styles.stickyMenu}
            style={{ width: UI.isIOS() && UI.isChrome() ? 'calc(100vw - 16px)' : '100vw' }}
        >
            <Flex
                alignItems='center'
                css={styles.box}
            >
                <button
                    css={styles.backToTop}
                    onClick={navigateToTop}
                >
                    <Flex
                        justifyContent='center'
                        alignItems='center'
                    >
                        <Chevron
                            direction='up'
                            size={8}
                        />
                        <Text css={styles.topText}>{getText('top')}</Text>
                    </Flex>
                </button>
                <Flex
                    justifyContent='space-between'
                    alignItems='center'
                    css={styles.links}
                    {...(localeUtils.isFRCanada() && { fontSize: 12 })}
                >
                    {MENU_ITEMS.map(item => (
                        <Link
                            css={[state.activeId === item.anchor && styles.activeLink]}
                            disabled={state.activeId === item.anchor}
                            key={item.anchor}
                            children={item.label}
                            onClick={navigateToAnchor(item.anchor)}
                        />
                    ))}
                </Flex>
            </Flex>
        </Box>
    );
};

const styles = {
    stickyMenu: {
        height: 44,
        backgroundColor: 'white',
        boxShadow: '0px 3px 5px 0px  rgba(0, 0, 0, 0.1)',
        marginLeft: -16,
        position: 'sticky',
        top: 54,
        zIndex: 9,
        transition: 'all .3s'
    },

    box: {
        height: '100%'
    },

    links: {
        width: '100%',
        padding: '0 19px'
    },

    backToTop: {
        width: 67,
        height: 44,
        backgroundColor: '#F6F6F8',
        flex: 'none'
    },

    topText: {
        marginLeft: 5
    },
    activeLink: {
        fontWeight: '700',
        borderBottom: '2px solid',
        lineHeight: 1.3
    }
};

export default wrapFunctionalComponent(MobileStickyMenu, 'MobileStickyMenu');
